import type { AxiosError } from 'axios';
import { apiClient } from './client';

export type SubscriptionPlan = 'gooey_plus_monthly' | 'gooey_plus_annual';

// Mirrors GET /billing/subscription. `entitled` is the single source of truth
// for whether premium features are unlocked; the rest is for the billing UI.
export interface BillingSummary {
  configured: boolean;
  entitled: boolean;
  status: string | null;
  plan: SubscriptionPlan | null;
  trialEnd: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  // True when entitlement is an admin-granted comp (no Stripe billing to
  // manage). The UI hides checkout/portal controls in this case.
  comp: boolean;
}

export const billingApi = {
  async getSubscription(): Promise<BillingSummary> {
    const response = await apiClient.get<BillingSummary>('/billing/subscription');
    return response.data;
  },

  // Returns the hosted Stripe Checkout URL — the caller redirects the browser.
  async startCheckout(plan: SubscriptionPlan): Promise<string> {
    const response = await apiClient.post<{ url: string }>('/billing/checkout-session', {
      plan,
    });
    return response.data.url;
  },

  // Returns the Stripe Customer Portal URL — the caller redirects the browser.
  async openPortal(): Promise<string> {
    const response = await apiClient.post<{ url: string }>('/billing/portal-session', {});
    return response.data.url;
  },
};

// True when an account-create failed because the caller hit their Gooey+ account
// cap (the API replies 403 with code "ACCOUNT_LIMIT"). Drives the upgrade prompt.
export function isAccountLimitError(err: unknown): boolean {
  const ax = err as AxiosError<{ code?: string }> | null;
  return ax?.response?.status === 403 && ax.response.data?.code === 'ACCOUNT_LIMIT';
}

// True when a delete failed because the target is the user's primary account
// (the API replies 409 with code "PRIMARY_ACCOUNT"). The primary can't be
// deleted — it's the always-present anchor.
export function isPrimaryAccountError(err: unknown): boolean {
  const ax = err as AxiosError<{ code?: string }> | null;
  return ax?.response?.status === 409 && ax.response.data?.code === 'PRIMARY_ACCOUNT';
}
